// Mantém user.perfil.permissao (localStorage) sincronizado com o backend sem
// obrigar a novo login. O validPermission continua a ler o localStorage; este
// módulo só o refresca quando:
//   - o backend avisa pelo WebSocket de notificações (perfil gravado);
//   - o separador volta a ficar visível, e a cada 5 minutos (rede de segurança);
//   - o router vai negar uma rota (confirma antes de bloquear).
// Quando algo muda emite o evento "permissoes-atualizadas" (o sidebar refaz o
// menu). Os botões da página aberta só mudam na próxima navegação; se a rota
// atual deixar de ser permitida, redireciona para o dashboard.
import axios from "axios";
import { toast } from "vue3-toastify";
import permissionMixin from "@/permissionMixin.js";
import notificationService from "@/services/notificationService";

const INTERVALO_MS = 5 * 60 * 1000;

let pedidoEmCurso = null;
let router = null;
let iniciado = false;

function lerUtilizador() {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
}

function autorizados(permissoes) {
  return (permissoes || [])
    .filter((p) => p.autorizacao)
    .map((p) => p.codigo)
    .sort()
    .join("|");
}

function rotaDashboard() {
  let tipo = null;
  try {
    tipo = JSON.parse(localStorage.getItem("tipo"));
  } catch {
    tipo = localStorage.getItem("tipo");
  }
  const tecnico = typeof tipo === "object" ? tipo?.nome === "tecnico" : tipo === "tecnico";
  return { name: tecnico ? "tecnicodashboard" : "dashboard" };
}

function verificarRotaAtual() {
  const rota = router?.currentRoute?.value;
  const codigo = rota?.meta?.codigo;
  if (!rota?.meta?.requiresAuth || !codigo) return;
  if (permissionMixin.methods.validPermission(codigo)) return;
  toast.warning("Deixou de ter acesso a esta página.");
  router.push(rotaDashboard());
}

// Devolve true se as permissões mudaram.
export function atualizarPermissoes() {
  if (pedidoEmCurso) return pedidoEmCurso;
  if (!lerUtilizador()?.token) return Promise.resolve(false);

  pedidoEmCurso = axios
    .get(`${process.env.VUE_APP_URL_BASE}/api/v1/perfil/me/permissoes/`)
    .then(({ data }) => {
      const perfil = data?.data?.perfil;
      const user = lerUtilizador();
      if (!perfil?.permissao || !user) return false;
      if (autorizados(user.perfil?.permissao) === autorizados(perfil.permissao)) return false;

      user.perfil = perfil;
      localStorage.setItem("user", JSON.stringify(user));
      window.dispatchEvent(new CustomEvent("permissoes-atualizadas"));
      toast.info("As suas permissões foram atualizadas. Os botões desta página atualizam ao mudar de página.");
      verificarRotaAtual();
      return true;
    })
    .catch(() => false)
    .finally(() => {
      pedidoEmCurso = null;
    });
  return pedidoEmCurso;
}

// Liga (ou religa, se mudou de utilizador) o WebSocket de notificações.
export function garantirLigacaoPermissoes() {
  const user = lerUtilizador();
  if (!user?.id || !user?.token) {
    if (notificationService.userId) notificationService.disconnect();
    notificationService.userId = null;
    return;
  }
  if (notificationService.userId && notificationService.userId !== user.id) {
    notificationService.disconnect();
  }
  if (!notificationService.state.isConnected && !notificationService.state.isConnecting) {
    notificationService.connect(user.id);
  }
}

export function iniciarSincronizacaoPermissoes(routerApp) {
  router = routerApp;
  if (iniciado) return;
  iniciado = true;

  notificationService.subscribe("permissoes", (msg) => {
    if (msg?.type === "permissoes_alteradas") atualizarPermissoes();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    // O WebSocket pode ter caído (suspensão, rede) e o serviço desiste ao fim
    // de 5 tentativas; religa e confirma o que possa ter mudado entretanto.
    garantirLigacaoPermissoes();
    atualizarPermissoes();
  });

  setInterval(() => {
    if (document.visibilityState !== "visible") return;
    garantirLigacaoPermissoes();
    atualizarPermissoes();
  }, INTERVALO_MS);
}
